// Module manifests — single source of truth for AppShell nav + routing.
//
// Per `LOOP_STATE.md ## Objective #4` (W2.3 frontend cross-cutting P0) +
// `ADR-0019` (PillNav 4+1 popover):
//   "src/lib/moduleManifests.ts drives AppShell icon-rail nav (no hardcoded
//    nav array in src/components/AppShell/AppShell.tsx)".
//
// W38.1 (`ADR-0076`) replaced the positional 5+4 slice with an explicit
// three-way `mobileSurface` classification. The slice was not merely
// inconvenient, it was **wrong** for the new layout: the pill has to be
// 首頁 / 日曆 / 心願, but `APP_SHELL_NAV` orders them
// `dashboard → calendar → diaries → albums → wishes → …`, so
// `slice(0, 3)` yields dashboard / calendar / **diaries** and 心願 lands in
// the overflow. Every module now declares where it lives on the mobile
// chrome, and the three sets are derived by filter instead of by index.
//
// The desktop icon-rail consumes `APP_SHELL_NAV` in order and is **not**
// affected: the array order is unchanged, only annotated.
//
// Module count is locked per `architecture.md §2` (per W33.0 ADR-0026,
// bumped from 8 to 9 to accommodate the W33.0 wishlist module). The W3
// bump to 8 added Albums (see `w3.md` §5.1 + `w2-frontend-components.md`
// §3.3). The W33.0 bump to 9 adds Wishlist (see `docs/spec/wishlist/
// DESIGN.md` §2.1 — 6 routes under `/wishes`).
//
// Layout strategy: 3 pill tabs + 1 avatar island + 4 menu entries + 1 account
// entry (3+1+4+1 = 9). Re-classifying a module = edit its `mobileSurface`
// here; if the counts in the guards below stop holding, that edit needs a new
// ADR.

import type { LucideIcon } from "lucide-react";
import type { IconName } from "@/components/ui/icons";
// W41.0 E4 (`ADR-0106`): 首頁 · 日曆 · 日記 · 心願 · 紀念日 moved to the
// self-drawn set. The four that remain — 相簿 · 配對 · 我 · 設定 — have no glyph
// yet; see the `icon` field's docstring for why borrowing one would be worse.
import {
  Image as ImageIcon,
  Settings,
  User,
  Users,
} from "lucide-react";

/**
 * Where a module lives on the mobile chrome (`ADR-0076`, extended by W52.1 D-3).
 *
 * - `pill`    — a tab inside the centre glass pill (the 3 daily drivers)
 * - `avatar`  — the left island, which is the current user's own profile
 * - `menu`    — an entry inside the right island's popover
 * - `account` — an entry inside the **avatar's** menu (W52.1 D-3)
 *
 * Exactly one surface per entry; the guards at the bottom of this file
 * enforce full coverage, so a new module cannot silently disappear from
 * mobile navigation.
 *
 * **`account` exists because 配對 is not content.** It sat in the popover
 * under 「關係與設定」, which is the one group whose only remaining member was
 * 設定 — a group of one, which is not a group. 配對 is the relationship layer,
 * the same class of thing as 「登出」, and it now sits in the avatar menu
 * beside it. Moving it also removed the app's only mobile entrance to `/pair`
 * for an already-paired user (the two other `<Link>`s to `/pair` are both
 * inside `Dashboard`'s `noPair` branch), which is why the avatar menu
 * gained the row rather than the popover merely losing one.
 */
export type ModuleSurface = "pill" | "menu" | "avatar" | "account";

/** Icon tint for a module's menu row. `ink` is the default everywhere. */
export type MenuAccent = "ink" | "you" | "partner" | "shared" | "gold";

export interface ModuleManifestEntry {
  /** Path the route is registered at in App.tsx (exact path; no params). */
  to: string;
  /**
   * Which icon set this module draws from.
   *
   * W41.0 E4 (`ADR-0106`) introduced a 13-glyph self-drawn set at 1.9px
   * round-capped, replacing lucide across the AppShell. **The set is not yet
   * complete for this manifest**: it was drawn against the mockup nav's five
   * surfaces (首頁 / 日曆 / 日記 / 心願 / 更多), while `APP_SHELL_NAV` declares nine
   * modules. 相簿 · 配對 · 我 · 設定 · 統計報告 · 主題 have no glyph yet and stay
   * on lucide until the set grows — deliberately, because borrowing `heart` for
   * 相簿 or `info` for 統計 would be a *wrong* icon, which is worse than a
   * mismatched stroke width.
   *
   * So the field is a two-state union rather than a rename: `"self" | "lucide"`.
   * Widening it to a third state later means drawing the missing six.
   */
  icon: { set: "self"; name: IconName } | { set: "lucide"; component: LucideIcon };
  /** 繁體中文 label used in nav + ARIA. */
  label: string;
  /** `data-testid` for stable E2E selection. */
  testId: string;
  /** Which mobile island this module belongs to (`ADR-0076`). */
  mobileSurface: ModuleSurface;
  /** Menu-row icon tint; only read when `mobileSurface === "menu"`. */
  menuAccent?: MenuAccent;
  /**
   * Where this route sends an **already-paired** user, when it does.
   *
   * W39.80. `routes/Pair.tsx:37` renders `<Navigate to="/settings/pair" replace />`
   * for anyone with a `pairId`, so the 配對 row is a working link that ends up
   * back where it started. A tool that asks "did the URL change?" cannot tell
   * that from a dead row, and the mobile UX sweep duly filed
   * `menu no-op: nav-pair-popover UNEXPECTED` on `/settings/pair` and
   * `/pair/invite` — while printing `landed /settings/pair` on the same line,
   * which is the redirect's own evidence.
   *
   * Declared here so the detector can **ask** instead of guessing. `/pair/invite`
   * redirects the same way, and it is the same component with a prop, so the
   * field takes the target rather than a boolean.
   */
  redirectsWhenPairedTo?: string;
}

/**
 * Manifest of all top-level modules (per `architecture.md §2` + ADR-0026).
 * Order = visual order in the desktop icon-rail. Per W33.0 the count bumped
 * 8 → 9; per W38.1 (ADR-0076) each entry also declares its mobile surface:
 * 3 in the centre pill, 1 behind the avatar island, 5 in the popover.
 */
export const APP_SHELL_NAV: readonly ModuleManifestEntry[] = Object.freeze([
  { to: "/dashboard", icon: { set: "self", name: "home" }, label: "首頁", testId: "nav-dashboard", mobileSurface: "pill" },
  { to: "/calendar", icon: { set: "self", name: "calendar" }, label: "日曆", testId: "nav-calendar", mobileSurface: "pill" },
  { to: "/diaries", icon: { set: "self", name: "book" }, label: "日記", testId: "nav-diaries", mobileSurface: "menu", menuAccent: "ink" },
  { to: "/albums", icon: { set: "lucide", component: ImageIcon }, label: "相簿", testId: "nav-albums", mobileSurface: "menu", menuAccent: "ink" },
  { to: "/wishes", icon: { set: "self", name: "star" }, label: "心願", testId: "nav-wishes", mobileSurface: "pill" },
  { to: "/anniversaries", icon: { set: "self", name: "heart" }, label: "紀念日", testId: "nav-anniversaries", mobileSurface: "menu", menuAccent: "gold" },
  /**
   * W52.1 D-3 — was `"menu"`, where it sat under a group labelled
   * 「關係與設定」 that would have been left holding one item.
   */
  {
    to: "/pair",
    icon: { set: "lucide", component: Users },
    label: "配對",
    testId: "nav-pair",
    mobileSurface: "account",
    redirectsWhenPairedTo: "/settings/pair",
  },
  { to: "/profile", icon: { set: "lucide", component: User }, label: "我", testId: "nav-profile", mobileSurface: "avatar" },
  { to: "/settings", icon: { set: "lucide", component: Settings }, label: "設定", testId: "nav-settings", mobileSurface: "menu", menuAccent: "ink" },
]);

if (APP_SHELL_NAV.length !== 9) {
  throw new Error(
    `APP_SHELL_NAV must be 9 modules per architecture.md §2 + ADR-0026; got ${APP_SHELL_NAV.length}`,
  );
}

/** The centre pill's tabs — 首頁 / 日曆 / 心願. */
export const MOBILE_PILL_ITEMS: readonly ModuleManifestEntry[] = Object.freeze(
  APP_SHELL_NAV.filter((m) => m.mobileSurface === "pill"),
);

/**
 * The left island: the signed-in user's own profile.
 *
 * It is the **only** entry point to `/profile` on mobile. `ADR-0019` deleted
 * the `SubRowNav` utility strip precisely because "pair / profile / settings
 * rendered twice (popover + sub-row)" — keeping a 我 row in the popover as
 * well would walk straight back into that.
 */
export const MOBILE_AVATAR_ITEM: ModuleManifestEntry = (() => {
  const found = APP_SHELL_NAV.filter((m) => m.mobileSurface === "avatar");
  if (found.length !== 1) {
    throw new Error(
      `exactly one module must be marked mobileSurface:"avatar" (ADR-0076); got ${found.length}`,
    );
  }
  const [entry] = found;
  // Unreachable given the length check above; `noUncheckedIndexedAccess`
  // cannot see the correlation, and a non-null assertion here would be a lie
  // the compiler is right not to believe.
  if (!entry) {
    throw new Error("MOBILE_AVATAR_ITEM: the single avatar-surface module could not be read");
  }
  if (entry.to !== "/profile") {
    throw new Error(
      `the avatar island is the profile surface; expected /profile, got ${entry.to}`,
    );
  }
  return entry;
})();

export interface MobileMenuGroup {
  /** 繁體中文 group heading, also the `aria-label` of the `role="group"`. */
  label: string;
  items: readonly ModuleManifestEntry[];
}

/**
 * Resolve menu entries by route, asserting each one really is a menu entry.
 * Keeping this a lookup (rather than a second hand-written array) means the
 * manifest stays the only place a module's label / icon / accent is declared.
 */
function menuItemsIn(paths: readonly string[]): readonly ModuleManifestEntry[] {
  return Object.freeze(
    paths.map((path) => {
      const entry = APP_SHELL_NAV.find((m) => m.to === path);
      if (!entry) {
        throw new Error(`MOBILE_MENU_GROUPS references an unknown route: ${path}`);
      }
      if (entry.mobileSurface !== "menu") {
        throw new Error(
          `${path} is listed in MOBILE_MENU_GROUPS but its mobileSurface is "${entry.mobileSurface}"`,
        );
      }
      return entry;
    }),
  );
}

/**
 * The right island's popover body: the daily content modules.
 *
 * W52.1 D-2/D-3 — this was two groups, 內容 (3) + 關係與設定 (配對 · 設定),
 * followed by a separator and two `即將` placeholders. All three parts are gone:
 * the placeholders had no route and no decision behind them (the docstring
 * claiming they were "the one sanctioned exception" cites `DESIGN.md §18.7`,
 * which says the opposite — "移除 stub UI 比保留 dead-end 更正確"), and
 * moving 配對 to the avatar menu left 關係與設定 holding exactly one item,
 * which is not a group.
 *
 * `menuItemsIn` asserts each path really is a `"menu"` entry, so putting
 * `/pair` here would throw at import rather than silently render a row that
 * does not light up.
 */
export const MOBILE_MENU_GROUPS: readonly MobileMenuGroup[] = Object.freeze([
  { label: "內容", items: menuItemsIn(["/diaries", "/albums", "/anniversaries", "/settings"]) },
]);

/**
 * W52.1 D-3 — the rows that live in the avatar's menu rather than the nav's.
 *
 * Derived by filter for the same reason `MOBILE_PILL_ITEMS` is: a second
 * hand-written array is a second place for a module's placement to be
 * declared, and the two can disagree. `AvatarMenu` reads this, so adding a
 * relationship-layer module is a one-line manifest edit.
 */
export const MOBILE_ACCOUNT_ITEMS: readonly ModuleManifestEntry[] = Object.freeze(
  APP_SHELL_NAV.filter((m) => m.mobileSurface === "account"),
);

/**
 * Coverage guard: pill + menu + avatar + account must account for all 9 modules
 * with no orphan and no duplicate. This is the invariant that the old positional
 * slice could not express — it only ever knew about "first N" and "the rest".
 */
const groupedMenuPaths = MOBILE_MENU_GROUPS.flatMap((g) => g.items.map((m) => m.to));
const menuEntries = APP_SHELL_NAV.filter((m) => m.mobileSurface === "menu");

if (groupedMenuPaths.length !== menuEntries.length) {
  throw new Error(
    `MOBILE_MENU_GROUPS covers ${groupedMenuPaths.length} of ${menuEntries.length} menu modules`,
  );
}
for (const path of groupedMenuPaths) {
  if (groupedMenuPaths.filter((p) => p === path).length !== 1) {
    throw new Error(`MOBILE_MENU_GROUPS lists ${path} more than once`);
  }
}

/**
 * W52.1 D-3 — every `"account"` module must actually reach the avatar menu.
 *
 * The counterpart to the coverage check above, and the one that would have
 * caught D-3 at the moment it was introduced: a module reclassified to
 * `"account"` that `AvatarMenu` never renders is a module with no mobile
 * entrance at all, which is the orphan-route defect this repo has already paid
 * for once (ADR-0170).
 */
const accountEntries = APP_SHELL_NAV.filter((m) => m.mobileSurface === "account");
if (MOBILE_ACCOUNT_ITEMS.length !== accountEntries.length) {
  throw new Error(
    `MOBILE_ACCOUNT_ITEMS covers ${MOBILE_ACCOUNT_ITEMS.length} of ${accountEntries.length} account modules`,
  );
}

if (MOBILE_PILL_ITEMS.length !== 3) {
  throw new Error(
    `the centre pill holds 3 daily drivers per ADR-0076; got ${MOBILE_PILL_ITEMS.length}`,
  );
}

/**
 * Is `pathname` the module at `route`, or one of its screens?
 *
 * This exists because the mobile islands have to decide "am I inside this
 * module?" from the location alone, and the answer is not an equality test:
 * `/settings` owns `/settings/account`, `/settings/pair` and
 * `/settings/pair/dissolve`, and `/pair` owns `/pair/invite`. Comparing with
 * `===` left the menu island dark on all of those — you would land on the
 * account screen with no navigation element saying where you are.
 *
 * It mirrors `<NavLink>`'s default (prefix) matching on purpose: the popover
 * row for 設定 lights via NavLink, so the island has to agree with it. The
 * trailing-slash boundary is what keeps a hypothetical `/settings-archive`
 * from being counted as inside `/settings`.
 */
export function isWithinModule(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}
